'use client';

import { useEffect } from "react";

type YouTubeVideoSectionProps = {
  video: {
    title?: string;
    youtubeUrl: string;
    thumbnail?: string;
    description?: string;
    aspectRatio?: '16:9' | '4:3' | '1:1';
    autoplay?: boolean;
    showControls?: boolean;
  };
  add_background?: boolean;
};

export function YouTubeVideoSection({ video, add_background = false }: YouTubeVideoSectionProps) {
  // Extract video ID from YouTube URL
  const getVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getVideoId(video.youtubeUrl);
  useEffect(() => {
    // Any side effects if needed
    console.log('YouTubeVideoSection mounted or updated', { video, add_background });
  }, [video, add_background]);
  const content = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      {/* Left: Title and Description with orange background */}
      <div className="bg-[url('/images/right-corner-orange-supergrafis.svg')] bg-no-repeat bg-right-top">
        {video.title && (
          <h2 className="text-3xl md:text-3xl font-bold text-primary-700 mt-12">
            {video.title}
          </h2>
        )}
        {video.description && (
          <div 
            className="mt-4 text-gray-600 text-[18px] leading-relaxed max-w-lg [&_p]:mb-[18px]"
            dangerouslySetInnerHTML={{ __html: video.description }}
          />
        )}
      </div>
      
      {/* Right: Video */}
      <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-md">
        {videoId && (
          <iframe
            width="560"
            height="315"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=${video.autoplay ? '1' : '0'}&controls=${video.showControls ? '1' : '0'}`}
            title={video.title || 'YouTube video player'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        )}
      </div>
    </div>
  );

  if (add_background) {
    return (
      <section
        className="
          w-screen relative left-1/2 right-1/2 -mx-[40vw]
          bg-[linear-gradient(to_bottom,rgba(237,237,249,0),#DEE5E8)]
          my-8 py-8
        "
      >
        <div className="bg-[url('/images/left-corner-supergrafis.png')] bg-no-repeat bg-left-bottom bg-[length:400px] py-8">
          <div className="container max-w-7xl mx-auto items-start gap-8 flex">
            <div className="flex-1 min-w-0 px-10">
              {content}
            </div>
            <div className="w-80 flex-shrink-0 lg:block"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="w-full">
      {content}
    </div>
  );
}
